import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchWorkOrder,
  fetchWorkOrders,
  createWorkOrder,
  updateWorkOrder,
  markWorkOrderReadyForInvoicing,
} from "../api/work-order";
import produce from "immer";

// API work order JSON may include `item_list_product_status` (handling list pick progress).

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  workOrder: {},
  data: [],
  count: 0,
  currentCount: 0,
};

export const workOrdersController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

export const updateWorkOrderAsync = createAsyncThunk(
  "workOrders/updateWorkOrderAsync",
  async (payload) => {
    let response = await updateWorkOrder(payload);

    // If errors, reject and return response
    if (response.error) return false;

    // If all good, return response.data (not the full response object)
    return response.data;
  }
);

export const createWorkOrderAsync = createAsyncThunk(
  "workOrders/createWorkOrderAsync",
  async (payload) => {
    let response = await createWorkOrder(payload);

    // If errors, reject and return response
    if (response.error) return false;

    // If all good, return response.data (not the full response object)
    return response.data;
  }
);

export const getWorkOrders = createAsyncThunk(
  "workOrders/getWorkOrders",
  async (controller = workOrdersController, { rejectWithValue }) => {
    let response = await fetchWorkOrders(controller);

    // If errors, reject and return response
    if (response.error) return rejectWithValue(response);

    // If all good, return response.data (not the full response object)
    return response.data;
  }
);

export const getWorkOrderAsync = createAsyncThunk(
  "workOrders/getWorkOrderAsync",
  async (id, { rejectWithValue }) => {
    let response = await fetchWorkOrder(id);

    // If errors, reject and return response
    if (response.error) return rejectWithValue(response);

    // If all good, return response.data (not the full response object)
    return response.data;
  }
);

export const markWorkOrderReadyForInvoicingAsync = createAsyncThunk(
  "workOrders/markWorkOrderReadyForInvoicingAsync",
  async ({ id, billing_preparation }, { rejectWithValue }) => {
    try {
      const response = await markWorkOrderReadyForInvoicing(
        id,
        billing_preparation
      );
      const data = response?.data?.data ?? response?.data;
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: err.message });
    }
  }
);

const workOrderSlice = createSlice({
  name: "workOrders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getWorkOrders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getWorkOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data || action.payload;
        state.count = action.payload.count || 0;
        state.currentCount = action.payload.currentCount || 0;
      })
      .addCase(getWorkOrders.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["Something went wrong....is controller missing?"];
      })
      .addCase(getWorkOrderAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getWorkOrderAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.workOrder = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getWorkOrderAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = [
          "Something went wrong when trying to fetch work order.",
        ];
      })
      .addCase(updateWorkOrderAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateWorkOrderAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.workOrder = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateWorkOrderAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = [
          "Something went wrong when trying to update work order.",
        ];
      })
      .addCase(markWorkOrderReadyForInvoicingAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(markWorkOrderReadyForInvoicingAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.workOrder = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(markWorkOrderReadyForInvoicingAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["work_orders.billing_ready.failed"];
      });
  },
});

export const { reducer } = workOrderSlice;

export default workOrderSlice;

export const selectWorkOrderById = (state, workOrderId) =>
  state.workOrders.data.find((workOrder) => workOrder.id == workOrderId);
